"use client";
// [ERP-DASHBOARD-WORK-INSTRUCTIONS-BOARD-CHAT-COMPLETE-FLOW-DEV-20260622-001]
// 업무지시 보드 — 구역(공통/강원준/강동준)별 타이틀 목록 → 상세 모달(세부내용+채팅+종료체크/승인).
// 확인필요는 instruction.status=needs_check 자체 처리(주문 확인필요와 분리). 구역명 고정 라벨.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getWorkInstructionBoard,
  createWorkInstruction,
  getWorkInstructionDetail,
  patchWorkInstruction,
  deleteWorkInstruction,
  postWorkInstructionMessage,
  requestWorkInstructionDone,
  approveWorkInstruction,
  type DwiBoard,
  type DwiDetail,
  type DwiItem,
} from "@/lib/api";

const SECTION_ORDER = ["common", "kang_wonjun", "kang_dongjun"] as const;
const SECTION_LABEL: Record<string, string> = {
  common: "공통 지시사항",
  kang_wonjun: "강원준",
  kang_dongjun: "강동준",
};
const PAGE_SIZE = 5;

function Pager({ page, total, onChange }: { page: number; total: number; onChange: (p: number) => void }) {
  if (total <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-1.5 text-[11px] text-gray-500">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="px-1.5 disabled:opacity-30 hover:text-gray-700"
      >
        ‹
      </button>
      <span>{page} / {total}</span>
      <button
        type="button"
        disabled={page >= total}
        onClick={() => onChange(page + 1)}
        className="px-1.5 disabled:opacity-30 hover:text-gray-700"
      >
        ›
      </button>
    </div>
  );
}

function statusBadge(status: string) {
  switch (status) {
    case "needs_check":
      return { label: "확인필요", cls: "bg-orange-100 text-orange-700" };
    case "done_requested":
      return { label: "종료요청", cls: "bg-blue-100 text-blue-700" };
    case "approved":
      return { label: "완료", cls: "bg-green-100 text-green-700" };
    case "archived":
      return { label: "보관", cls: "bg-gray-100 text-gray-500" };
    default:
      return { label: "접수", cls: "bg-gray-100 text-gray-600" };
  }
}

function fmt(ts: string | null | undefined): string {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("ko-KR", {
      month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

export default function WorkInstructionsCard() {
  const [board, setBoard] = useState<DwiBoard | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedDone, setExpandedDone] = useState<Record<string, boolean>>({});
  // 구역별 독립 페이지 (진행중/완료이력 각각 5개씩)
  const [activePages, setActivePages] = useState<Record<string, number>>({ common: 1, kang_wonjun: 1, kang_dongjun: 1 });
  const [completedPages, setCompletedPages] = useState<Record<string, number>>({ common: 1, kang_wonjun: 1, kang_dongjun: 1 });

  const loadBoard = useCallback(async () => {
    try {
      setBoard(await getWorkInstructionBoard());
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  // 삭제/승인 등으로 목록 수가 줄어 현재 페이지가 범위를 벗어나면 마지막 페이지로 보정.
  useEffect(() => {
    if (!board) return;
    const clamp = (prev: Record<string, number>, pick: (s: { active: DwiItem[]; completed: DwiItem[] }) => DwiItem[]) => {
      let changed = false;
      const next = { ...prev };
      for (const sec of board.sections) {
        const total = Math.max(1, Math.ceil(pick(sec).length / PAGE_SIZE));
        const cur = prev[sec.key] ?? 1;
        const fixed = Math.min(Math.max(1, cur), total);
        if (fixed !== cur) {
          next[sec.key] = fixed;
          changed = true;
        }
      }
      return changed ? next : prev;
    };
    setActivePages((p) => clamp(p, (s) => s.active));
    setCompletedPages((p) => clamp(p, (s) => s.completed));
  }, [board]);

  // 백엔드 엔드포인트가 살아있을 때만 렌더(운영 미반영 환경 영향 0).
  if (loadError || !board) return null;

  return (
    <section className="mb-6">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-gray-800">📌 업무지시</h2>
            <span className="text-[10px] text-gray-400">제목을 클릭하면 상세·채팅이 열립니다</span>
          </div>
          {board.can_manage && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              + 새 지시
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SECTION_ORDER.map((key) => {
            const sec = board.sections.find((s) => s.key === key);
            const active = sec?.active ?? [];
            const completed = sec?.completed ?? [];
            const needsCount = active.filter((i) => i.status === "needs_check").length;
            const doneReqCount = active.filter((i) => i.status === "done_requested").length;
            const isOwn = board.own_section === key;
            const activeTotalPages = Math.max(1, Math.ceil(active.length / PAGE_SIZE));
            const activePage = Math.min(activePages[key] ?? 1, activeTotalPages);
            const activeVisible = active.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE);
            const completedTotalPages = Math.max(1, Math.ceil(completed.length / PAGE_SIZE));
            const completedPage = Math.min(completedPages[key] ?? 1, completedTotalPages);
            const completedVisible = completed.slice((completedPage - 1) * PAGE_SIZE, completedPage * PAGE_SIZE);
            return (
              <div
                key={key}
                className={`border rounded-lg p-3 ${isOwn ? "border-blue-200 bg-blue-50/40" : "border-gray-100"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-gray-700">
                    {SECTION_LABEL[key]}
                    {isOwn && <span className="ml-1 text-[9px] text-blue-500">담당</span>}
                  </div>
                  <div className="flex gap-1">
                    {needsCount > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">확인 {needsCount}</span>
                    )}
                    {doneReqCount > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">종료요청 {doneReqCount}</span>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  {active.length === 0 && (
                    <div className="text-[11px] text-gray-300 py-2">진행중 지시 없음</div>
                  )}
                  {activeVisible.map((it) => {
                    const b = statusBadge(it.status);
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setOpenId(it.id)}
                        className="w-full text-left bg-white border border-gray-200 rounded-md px-2.5 py-1.5 hover:border-blue-300 transition"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-gray-800 truncate">{it.title}</span>
                          <span className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded ${b.cls}`}>{b.label}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5 text-[10px] text-gray-400">
                          <span>{it.created_by_name ?? ""}</span>
                          <span>
                            {it.message_count > 0 && <span className="mr-1">💬{it.message_count}</span>}
                            {fmt(it.last_message_at ?? it.created_at)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <Pager
                  page={activePage}
                  total={activeTotalPages}
                  onChange={(p) => setActivePages((prev) => ({ ...prev, [key]: p }))}
                />

                {completed.length > 0 && (
                  <div className="mt-2 border-t border-gray-100 pt-2">
                    <button
                      type="button"
                      onClick={() => setExpandedDone((e) => ({ ...e, [key]: !e[key] }))}
                      className="text-[10px] text-gray-500 hover:text-gray-700"
                    >
                      완료 이력 {completed.length}건 {expandedDone[key] ? "▴" : "▾"}
                    </button>
                    {expandedDone[key] && (
                      <>
                        <div className="space-y-1 mt-1">
                          {completedVisible.map((it) => (
                            <button
                              key={it.id}
                              type="button"
                              onClick={() => setOpenId(it.id)}
                              className="w-full text-left text-[11px] text-gray-500 hover:text-gray-700 truncate"
                            >
                              ✓ {it.title}
                              <span className="text-gray-300 ml-1">{fmt(it.approved_at)}</span>
                            </button>
                          ))}
                        </div>
                        <Pager
                          page={completedPage}
                          total={completedTotalPages}
                          onChange={(p) => setCompletedPages((prev) => ({ ...prev, [key]: p }))}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(item) => {
            setShowCreate(false);
            setActivePages((p) => ({ ...p, [item.section_key]: 1 })); // 새 지시 구역은 1페이지
            loadBoard();
          }}
        />
      )}
      {openId != null && (
        <DetailModal
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => loadBoard()}
        />
      )}
    </section>
  );
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (item: DwiItem) => void }) {
  const [section, setSection] = useState<string>("common");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!title.trim()) {
      setErr("제목을 입력하세요.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await createWorkInstruction({ section_key: section, title: title.trim(), content });
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "생성 실패");
      setBusy(false);
    }
  }

  return (
    <ModalShell title="새 업무지시" onClose={onClose}>
      <label className="block text-[11px] text-gray-500 mb-1">구역</label>
      <select
        value={section}
        onChange={(e) => setSection(e.target.value)}
        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 mb-3"
      >
        {SECTION_ORDER.map((k) => (
          <option key={k} value={k}>{SECTION_LABEL[k]}</option>
        ))}
      </select>
      <label className="block text-[11px] text-gray-500 mb-1">지시 제목</label>
      <input
        value={title}
        maxLength={200}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="예) 9월 발주 정리"
        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 mb-3"
      />
      <label className="block text-[11px] text-gray-500 mb-1">세부 내용</label>
      <textarea
        value={content}
        maxLength={5000}
        rows={5}
        onChange={(e) => setContent(e.target.value)}
        placeholder="세부 지시 내용"
        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 mb-3 resize-y"
      />
      {err && <div className="text-xs text-red-500 mb-2">{err}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500">취소</button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg font-medium"
        >
          {busy ? "저장 중…" : "생성"}
        </button>
      </div>
    </ModalShell>
  );
}

function DetailModal({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const [detail, setDetail] = useState<DwiDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false); // 동기 lock — Enter 연타/더블클릭 중복 전송 차단
  const msgEndRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await getWorkInstructionDetail(id);
      setDetail(d);
      setEditTitle(d.instruction.title);
      setEditContent(d.instruction.content);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "로드 실패");
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ block: "end" });
  }, [detail?.messages.length]);

  async function send() {
    // 단일 전송 경로 — Enter / 버튼 모두 이 함수만 호출.
    if (sendingRef.current) return; // 동기 lock(전송 중 무시)
    const text = chatInput.trim();
    if (!text) return; // 빈 메시지 금지
    sendingRef.current = true;
    setSending(true);
    try {
      await postWorkInstructionMessage(id, text);
      setChatInput("");
      await load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "전송 실패");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "처리 실패");
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (!window.confirm("이 업무지시를 삭제하시겠습니까?")) return;
    setBusy(true);
    try {
      await deleteWorkInstruction(id);
      onChanged();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "삭제 실패");
      setBusy(false);
    }
  }

  const instr: DwiItem | undefined = detail?.instruction;
  const b = instr ? statusBadge(instr.status) : null;

  return (
    <ModalShell title={instr ? SECTION_LABEL[instr.section_key] : "업무지시"} onClose={onClose} wide>
      {!detail || !instr ? (
        <div className="text-xs text-gray-400 py-6">{err ?? "불러오는 중…"}</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-4 max-h-[70vh]">
          {/* 좌: 지시 본문 */}
          <div className="md:w-1/2 overflow-y-auto">
            <div className="flex items-center gap-2 mb-2">
              {b && <span className={`text-[10px] px-2 py-0.5 rounded ${b.cls}`}>{b.label}</span>}
              <span className="text-[10px] text-gray-400">
                {instr.created_by_name} · {fmt(instr.created_at)}
              </span>
            </div>
            {editing ? (
              <>
                <input
                  value={editTitle}
                  maxLength={200}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full text-sm font-semibold border border-gray-300 rounded-md px-2 py-1.5 mb-2"
                />
                <textarea
                  value={editContent}
                  maxLength={5000}
                  rows={8}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 resize-y"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => act(() => patchWorkInstruction(id, { title: editTitle, content: editContent })).then(() => setEditing(false))}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md"
                  >
                    저장
                  </button>
                  <button type="button" onClick={() => setEditing(false)} className="px-3 py-1 text-xs text-gray-500">취소</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-base font-bold text-gray-900 mb-2">{instr.title}</h3>
                <div className="text-sm text-gray-700 whitespace-pre-wrap break-words mb-3">
                  {instr.content || <span className="text-gray-300">세부 내용 없음</span>}
                </div>
              </>
            )}

            {instr.status === "approved" && (
              <div className="text-[11px] text-green-600 mb-2">
                ✓ 승인: {instr.approved_by_name} · {fmt(instr.approved_at)}
              </div>
            )}
            {instr.status === "done_requested" && instr.done_requested_by_name && (
              <div className="text-[11px] text-blue-600 mb-2">종료요청: {instr.done_requested_by_name}</div>
            )}

            <div className="flex flex-wrap gap-2 mt-2">
              {detail.can_request_done && (
                <button type="button" disabled={busy} onClick={() => act(() => requestWorkInstructionDone(id))}
                  className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md">
                  업무 종료 체크
                </button>
              )}
              {detail.can_approve && (
                <button type="button" disabled={busy} onClick={() => act(() => approveWorkInstruction(id))}
                  className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md">
                  승인(완료)
                </button>
              )}
              {detail.can_edit && !editing && (
                <button type="button" onClick={() => setEditing(true)}
                  className="px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded-md">수정</button>
              )}
              {detail.can_edit && (
                <button type="button" disabled={busy} onClick={doDelete}
                  className="px-3 py-1.5 text-xs border border-red-200 text-red-500 rounded-md">삭제</button>
              )}
            </div>
            {err && <div className="text-xs text-red-500 mt-2">{err}</div>}
          </div>

          {/* 우: 채팅 */}
          <div className="md:w-1/2 flex flex-col border-l border-gray-100 md:pl-4">
            <div className="text-xs font-semibold text-gray-500 mb-2">채팅</div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[200px]">
              {detail.messages.length === 0 && (
                <div className="text-[11px] text-gray-300">아직 채팅이 없습니다.</div>
              )}
              {detail.messages.map((m) => (
                <div key={m.id} className="text-xs">
                  <div className="flex items-baseline gap-1">
                    <span className="font-semibold text-gray-700">{m.sender_name}</span>
                    <span className="text-[10px] text-gray-400">{fmt(m.created_at)}</span>
                  </div>
                  <div className="text-gray-800 whitespace-pre-wrap break-words">{m.message}</div>
                </div>
              ))}
              <div ref={msgEndRef} />
            </div>
            {instr.status === "approved" || instr.status === "archived" ? (
              <div className="text-[11px] text-gray-400 mt-2">완료된 업무지시입니다.</div>
            ) : (
              <div className="flex gap-2 mt-2">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    // 한글 IME 조합 중 Enter(조합 확정)는 전송하지 않음 → 중복 전송 차단.
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      e.stopPropagation();
                      send();
                    }
                  }}
                  rows={2}
                  placeholder="메시지 입력 (Enter 전송, Shift+Enter 줄바꿈)"
                  className="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1.5 resize-none"
                />
                <button type="button" disabled={sending || !chatInput.trim()} onClick={send}
                  className="px-3 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md">전송</button>
              </div>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    /* [ERP-GLOBAL-MODAL-OUTSIDE-CLICK-CLOSE-DISABLE-DEV-20260623-001] overlay 클릭 닫기 제거 */
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-md"} p-5`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-800">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
